import { useCallback, useEffect, useState } from 'react';

import { useEventTracking } from '@hooks';
import { ImpressionElement, Page, impression } from '@tracking';

interface UseVideoPlaybackTrackingProps {
  trackingPage: Page;
  videoRef: React.RefObject<HTMLVideoElement>;
}

const useVideoPlaybackTracking = ({ trackingPage, videoRef }: UseVideoPlaybackTrackingProps) => {
  const [triggerVideoStarted, setTriggerVideoStarted] = useState<boolean>(false);
  const [triggeredHalfViewed, setTriggeredHalfViewed] = useState<boolean>(false);
  const { trackEvent } = useEventTracking({ page: trackingPage });

  const handlePlayStart = useCallback(() => {
    if (!triggerVideoStarted) {
      setTriggerVideoStarted(true);
      trackEvent(
        impression({ impressionElement: ImpressionElement.Video, context: 'video start' })
      );
    }
  }, [trackEvent, triggerVideoStarted]);

  const handlePlayStop = useCallback(() => {
    trackEvent(impression({ impressionElement: ImpressionElement.Video, context: 'video stop' }));
  }, [trackEvent]);

  const handlePlayedHalf = useCallback(() => {
    if (
      !triggeredHalfViewed &&
      videoRef.current.duration &&
      videoRef.current.currentTime / videoRef.current.duration >= 0.5
    ) {
      setTriggeredHalfViewed(true);
      trackEvent(impression({ impressionElement: ImpressionElement.Video, context: 'video 50%' }));
    }
  }, [trackEvent, triggeredHalfViewed, videoRef]);

  useEffect(() => {
    const videoPlayer = videoRef.current;

    if (videoPlayer) {
      videoPlayer.addEventListener('play', handlePlayStart);
      videoPlayer.addEventListener('ended', handlePlayStop);
      videoPlayer.addEventListener('timeupdate', handlePlayedHalf);
    }

    return () => {
      if (videoPlayer) {
        videoPlayer.removeEventListener('play', handlePlayStart);
        videoPlayer.removeEventListener('ended', handlePlayStop);
        videoPlayer.removeEventListener('timeupdate', handlePlayedHalf);
      }
    };
  }, [handlePlayedHalf, handlePlayStart, handlePlayStop, videoRef]);
};

export default useVideoPlaybackTracking;
